import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MenuInterface } from '../../../core/interfaces/user.interface';
import { CurrentUserService } from '../../../core/services/Current-user/currentuser.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-view-menu',
  templateUrl: './view-menu.component.html',
  styleUrls: ['./view-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewMenuComponent implements OnInit {
  menu: MenuInterface[] = [];
  isDropdownOpen = false;
  currentPath: string = '';

  openedSubMenus = new Set<string>();


  private readonly hasItemCache = new WeakMap<MenuInterface, boolean>();

  constructor(
    private currentUserService: CurrentUserService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    // Subscribe to router events to update active state
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.currentPath = this.router.url;
      this.updateOpenedSubMenus(this.menu);
      this.cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    this.currentPath = this.router.url;
    this.currentUserService.menuReady$.subscribe(() => {
      this.menu = this.currentUserService.user.menu;
      this.updateOpenedSubMenus(this.menu);
      this.cdr.markForCheck();
    });
  }


  private readonly rootContextCache = new WeakMap<MenuInterface, { $implicit: MenuInterface[] }>();

  rootContext(item: MenuInterface): { $implicit: MenuInterface[] } {
    let context = this.rootContextCache.get(item);
    if (!context) {
      context = { $implicit: [item] };
      this.rootContextCache.set(item, context);
    }
    return context;
  }

  onSubMenuOpenChange(title: string, open: boolean): void {
    if (open) {
      this.openedSubMenus.add(title);
    } else {
      this.openedSubMenus.delete(title);
    }
    this.cdr.markForCheck();
  }

  trackNode(index: number, item: MenuInterface) {
    return item;
  }

  trackTable(index: number, table: any) {
    return table.id;
  }

  // Check if a menu item should be active
  isActive(item: any): boolean {
    if (item.tables) {
      return item.tables.some(
        (table: any) =>
          this.currentPath === `/dynamic/${table.id}/chart` ||
          this.currentPath === `/dynamic/${table.id}/table`,
      );
    }
    return false;
  }

  // Check if a submenu contains the active item
  isSubmenuActive(item: MenuInterface): boolean {
    if (this.isActive(item)) return true;
    if (item.children) {
      return item.children.some((child) => this.isSubmenuActive(child));
    }
    return false;
  }

  // Update opened submenus based on current route
  private updateOpenedSubMenus(items: MenuInterface[], parentPath: string = ''): void {
    items.forEach((item) => {
      if (this.isSubmenuActive(item)) {
        this.openedSubMenus.add(item.title);
      }
      if (item.children) {
        this.updateOpenedSubMenus(item.children, parentPath + '/' + item.title);
      }
    });
  }

  ifHasItem(item: MenuInterface): boolean {
    const cached = this.hasItemCache.get(item);
    if (cached !== undefined) {
      return cached;
    }

    const result = this.computeHasItem(item);
    this.hasItemCache.set(item, result);
    return result;
  }

  private computeHasItem(item: MenuInterface): boolean {
    if (item.tables?.length > 0) return true;
    if (item.children) {
      return item.children.some((child) => this.ifHasItem(child));
    }
    return false;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }
}
